import {
    BarChart3,
    FolderOpen,
    Headphones,
    ShieldCheck,
} from 'lucide-react';

const pillars = [
    {
        icon: FolderOpen,
        title: 'Documents & Extraits officiels',
        iconColor: 'text-white',
        iconBg: 'bg-[#b20303]',
        glow: 'group-hover:shadow-[0_0_35px_rgba(17,24,32,0.16)]',
        number: '01',
        features: [
            'Extrait RC en ligne',
            'Suivi des mutations et radiations',
            'Alertes sur les changements au registre',
            'Archives et historique de vos recherches',
        ],
        text: "Obtenez rapidement les documents dont vous avez besoin pour vos démarches bancaires, contractuelles ou administratives, sans passer par le guichet cantonal.",
    },
    {
        icon: ShieldCheck,
        title: 'Accompagnement juridique & comptable',
        iconColor: 'text-white',
        iconBg: 'bg-[#b20303]',
        glow: 'group-hover:shadow-[0_0_35px_rgba(180,150,109,0.24)]',
        number: '02',
        features: [
            'Mise en relation avec des experts qualifiés',
            'Conseils personnalisés selon votre forme juridique',
            'Support pour vos démarches courantes (modifications statutaires, formalités RC)',
            'Suivi de vos échéances importantes',
        ],
        text: "Bénéficiez d'un accompagnement humain pour les décisions qui comptent, sans les frais d'un cabinet à l'heure.",
    },
    {
        icon: BarChart3,
        title: 'Conformité & Veille réglementaire',
        iconColor: 'text-white',
        iconBg: 'bg-[#b20303]',
        glow: 'group-hover:shadow-[0_0_35px_rgba(180,150,109,0.24)]',
        number: '03',
        features: [
            'Suivi des obligations légales de votre entreprise',
            'Alertes sur les évolutions réglementaires suisses',
            'Rappels d’échéances (TVA, RC, cotisations)',
            'Tableau de bord de vos obligations en cours',
        ],
        text: "Restez en règle sans y penser — Helvedoc vous alerte avant l'échéance, pas après.",
    },
    {
        icon: Headphones,
        title: 'Support & Accompagnement',
        iconColor: 'text-white',
        iconBg: 'bg-[#b20303]',
        glow: 'group-hover:shadow-[0_0_35px_rgba(235,0,0,0.2)]',
        number: '04',
        features: [
            'Assistance personnalisée par email',
            'Centre d’aide et guides pratiques',
            'Équipe dédiée à votre disposition',
            'Réponses rapides à vos questions administratives',
        ],
        text: 'Un accompagnement de proximité, pensé pour les besoins réels des PME.',
    },
];

export function ValorisationSection() {
    return (
        <section
            id="solutions"
            className="relative overflow-hidden bg-[#F7F6F3] py-16 sm:py-20 lg:py-24"
        >
            {/* Décor de fond */}
            <div className="pointer-events-none absolute inset-0">
                <div className="absolute -left-40 top-12 h-[420px] w-[420px] rounded-full bg-[#B4966D]/10 blur-[100px]" />

                <div className="absolute -right-32 bottom-0 h-[420px] w-[420px] rounded-full bg-[#b20303]/[0.05] blur-[110px]" />

                <div
                    className="absolute inset-0 opacity-[0.025]"
                    style={{
                        backgroundImage:
                            'linear-gradient(#111820 1px, transparent 1px), linear-gradient(90deg, #111820 1px, transparent 1px)',
                        backgroundSize: '48px 48px',
                    }}
                />
            </div>

            <div className="relative mx-auto max-w-[1320px] px-5 sm:px-7 lg:px-10">
                {/* Titre */}
                <div className="mb-10 flex flex-col gap-6 lg:mb-14 lg:flex-row lg:items-end lg:justify-between">
                    <div className="max-w-[850px]">
                        <div className="mb-5 inline-flex items-center gap-2.5 text-[11px] font-semibold uppercase tracking-[0.28em] text-[#b20303]">
                            <span className="h-px w-6 bg-[#b20303]" />

                            Nos services
                        </div>

                        <h2 className="max-w-[820px] text-[32px] font-semibold leading-[1.1] tracking-[-0.035em] text-[#111820] sm:text-[40px] lg:text-[48px]">
                            Un accompagnement complet pour la gestion
                            administrative, juridique et documentaire de votre
                            entreprise
                        </h2>
                    </div>
                </div>

                {/* Cartes */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                    {pillars.map((pillar) => {
                        const Icon = pillar.icon;

                        return (
                            <article
                                key={pillar.title}
                                className={`group relative flex h-full min-h-[440px] flex-col overflow-hidden rounded-[22px] border border-[#111820]/[0.06] bg-gradient-to-b from-white to-[#FBFAF8] p-6 shadow-[0_10px_30px_rgba(17,24,32,0.05)] backdrop-blur-xl transition-all duration-500 hover:-translate-y-1.5 hover:border-[#B4966D]/40 hover:shadow-[0_24px_60px_rgba(17,24,32,0.11)] ${pillar.glow}`}
                            >
                                {/* Ligne supérieure */}
                                <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#B4966D]/70 to-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

                                {/* Numéro décoratif */}
                                <div className="absolute -right-6 -top-8 font-serif text-[76px] font-medium italic tracking-[-0.06em] text-[#111820]/[0.03] transition-all duration-500 group-hover:-translate-x-2 group-hover:translate-y-2 group-hover:text-[#111820]/[0.05]">
                                    {pillar.number}
                                </div>

                                {/* Icône */}
                                <div className="relative mb-6 flex items-center justify-between">
                                    <div
                                        className={`flex h-12 w-12 items-center justify-center rounded-xl bg-gradient-to-br from-[#c40404] to-[#7a0202] shadow-[0_8px_18px_rgba(178,3,3,0.28)] transition-all duration-500 group-hover:-rotate-3 group-hover:scale-110 ${pillar.iconBg}`}
                                    >
                                        <Icon
                                            className={`h-5 w-5 ${pillar.iconColor}`}
                                            strokeWidth={1.8}
                                        />
                                    </div>

                                    <span className="font-serif text-[10px] italic tracking-[0.15em] text-slate-300">
                                        {pillar.number}
                                    </span>
                                </div>

                                {/* Titre carte */}
                                <h3 className="relative mb-4 min-h-[50px] text-[18px] font-semibold leading-[1.3] tracking-[-0.02em] text-[#111820]">
                                    {pillar.title}
                                </h3>

                                {/* Fonctionnalités */}
                                <ul className="relative mb-5 min-h-[128px] space-y-2.5">
                                    {pillar.features.map((item) => (
                                        <li
                                            key={item}
                                            className="flex items-start gap-2.5 text-[12px] leading-5 text-slate-700"
                                        >
                                            <span className="mt-[7px] flex h-1.5 w-1.5 shrink-0 rounded-full bg-[#B4966D] shadow-[0_0_0_4px_rgba(180,150,109,0.1)]" />

                                            <span>{item}</span>
                                        </li>
                                    ))}
                                </ul>

                                {/* Texte */}
                                <div className="mt-auto">
                                    <div className="mb-4 h-px bg-gradient-to-r from-[#E5E7EB] via-[#E5E7EB] to-transparent" />

                                    <p className="text-[12.5px] leading-[1.65] text-slate-600">
                                        {pillar.text}
                                    </p>

                                    <div className="mt-5 flex items-center gap-3">
                                        <span className="h-px w-7 bg-[#B4966D] transition-all duration-500 group-hover:w-12 group-hover:bg-[#b20303]" />

                                        <span className="h-1.5 w-1.5 rounded-full bg-[#B4966D] transition-colors duration-500 group-hover:bg-[#b20303]" />
                                    </div>
                                </div>

                                {/* Ring intérieur */}
                                <div className="pointer-events-none absolute inset-0 rounded-[22px] ring-1 ring-inset ring-white/60" />
                            </article>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}

export default ValorisationSection;